import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  EmptyState,
  Input,
  Modal,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useCredentials, usePersons } from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import { SelectField, validityWindow } from './Directory'
import { readOnlyReason } from '../lib/dryrun'
import { relativeTime, shortDateTime } from '../lib/format'
import type { CommandLog, Credential, CredentialOrder } from '../api/types'

export const CREDENTIAL_KINDS = ['card', 'tag', 'pin', 'qr', 'plate', 'phone'] as const

const kindLabels: Record<string, string> = {
  card: 'Card',
  tag: 'Fob',
  pin: 'PIN',
  qr: 'QR',
  plate: 'Plate',
  phone: 'Phone',
}

export function kindLabel(kind: string): string {
  return kindLabels[kind] ?? kind
}

/** The global last-use fields the helpers below read — a `Credential`, or a
 *  location card row (`LocationCredentialRow`) carrying the same fields. */
export type LastUse = Pick<Credential, 'last_used_at' | 'last_used_location_name' | 'last_used_device_name'>

/** "3d ago" / "Never used" — the shared phrasing for a credential's last-use
 *  moment (person page, credentials list). */
export function lastUsedRelative(c: LastUse): string {
  return c.last_used_at ? relativeTime(c.last_used_at) : 'Never used'
}

/** The absolute timestamp for a title/tooltip — undefined when never used
 *  (nothing to show). */
export function lastUsedTitle(c: LastUse): string | undefined {
  return c.last_used_at ? shortDateTime(c.last_used_at) : undefined
}

/** The location name wins over the device name when both are present
 *  (a location is more meaningful to an operator than a raw reader). */
export function lastUsedWhere(c: LastUse): string | null {
  return c.last_used_location_name ?? c.last_used_device_name ?? null
}

/** "Last used 3d ago at Sandalettgatan 28" / "Last used 3d ago" / "Never
 *  used" — the person page's phrasing (issue #51). */
export function lastUsedSummary(c: LastUse): string {
  if (!c.last_used_at) return 'Never used'
  const where = lastUsedWhere(c)
  return where ? `Last used ${relativeTime(c.last_used_at)} at ${where}` : `Last used ${relativeTime(c.last_used_at)}`
}

/** "3d ago at Sandalettgatan 28" / "3d ago" / "Never used" — the compact
 *  table-cell phrasing (location card, issue #54). */
export function lastUsedAtWhere(c: LastUse): string {
  if (!c.last_used_at) return 'Never used'
  const where = lastUsedWhere(c)
  return where ? `${relativeTime(c.last_used_at)} at ${where}` : relativeTime(c.last_used_at)
}

export function credentialStatusTone(status: string): 'ok' | 'danger' | 'warn' | 'muted' {
  switch (status) {
    case 'active':
      return 'ok'
    case 'blocked':
      return 'danger'
    case 'lost':
      return 'warn'
    default:
      return 'muted'
  }
}

/** Inline feedback for a block/unblock CommandLog: status chip + per-system results. */
export function CommandResultView({ command }: { command: CommandLog }) {
  const tone =
    command.status === 'succeeded' ? 'ok' : command.status === 'failed' ? 'danger' : 'warn'
  return (
    <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/60 p-3">
      <div className="flex items-center gap-2">
        <Chip tone={tone} label={`Command ${command.status}`} />
        <span className="text-[11px] text-ostrel-ink-muted">{command.kind.replace(/_/g, ' ')}</span>
        <span className="tnum ml-auto text-[10px] text-ostrel-ink-muted">{command.unique_id}</span>
      </div>
      {command.results.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {command.results.map((r, i) => (
            <li key={i} className="tnum flex flex-col gap-0.5 text-[11px]">
              <div className="flex items-center gap-2">
                <span className={r.ok ? 'text-ostrel-ok' : 'text-ostrel-danger'}>{r.ok ? '✓' : '✗'}</span>
                <span className="text-ostrel-ink">{r.system ?? 'local'}</span>
                <span className="text-ostrel-ink-muted">{r.detail}</span>
              </div>
              {(r.warnings ?? []).map((w) => (
                <p key={w.code} className="pl-4 text-ostrel-warn" data-warning={w.code}>
                  {w.message}
                </p>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const statusOptions = ['active', 'blocked', 'lost', 'returned'] as const

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tnum rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-1 focus:ring-ostrel-gold ${
        active
          ? 'border-ostrel-gold/50 bg-ostrel-gold/10 text-ostrel-gold'
          : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:text-ostrel-ink'
      }`}
    >
      {label}
    </button>
  )
}

export interface CredentialFormModalProps {
  open: boolean
  onClose: () => void
  /** When set, the credential is issued to this person (no holder picker). */
  person?: string
}

/** Issue-credential form — shared by the credentials list and the person detail page. */
export function CredentialFormModal({ open, onClose, person }: CredentialFormModalProps) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const persons = usePersons()

  const [holder, setHolder] = useState('')
  const [kind, setKind] = useState<string>('card')
  const [code, setCode] = useState('')
  const [codeBase, setCodeBase] = useState('')

  useEffect(() => {
    if (!open) return
    setHolder(person ?? '')
    setKind('card')
    setCode('')
    setCodeBase('')
  }, [open, person])

  const issue = useMutation({
    mutationFn: () =>
      api.createCredential({
        person: (person ?? holder) || null,
        kind,
        code: code.trim(),
        code_base: (codeBase || null) as 'hex' | 'dec' | null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope] })
      onClose()
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    issue.mutate()
  }

  return (
    <Modal open={open} title="Issue credential" onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {!person && (
          <SelectField
            label="Holder"
            value={holder}
            onChange={setHolder}
            options={[
              { value: '', label: 'Unassigned' },
              ...(persons.data ?? []).map((p) => ({ value: p.unique_id, label: p.display_name || p.unique_id })),
            ]}
          />
        )}
        <SelectField
          label="Kind"
          value={kind}
          onChange={setKind}
          options={CREDENTIAL_KINDS.map((k) => ({ value: k, label: kindLabel(k) }))}
        />
        <Input
          label="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Card number, PIN, plate…"
          required
          className="font-mono"
        />
        <SelectField
          label="Code base"
          value={codeBase}
          onChange={setCodeBase}
          options={[
            { value: '', label: 'Default' },
            { value: 'hex', label: 'Hex' },
            { value: 'dec', label: 'Decimal' },
          ]}
        />
        <p className="text-xs text-ostrel-ink-muted">
          A credential works while its holder&apos;s validity window is open — set the window on the person.
        </p>
        {issue.isError && <p className="text-xs text-ostrel-danger">{issue.error.message}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={issue.isPending || !code.trim()}>
            {issue.isPending ? 'Issuing…' : 'Issue credential'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default function CredentialsPage() {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [kindFilter, setKindFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [issueOpen, setIssueOpen] = useState(false)
  // undefined = unsorted (id order); toggles asc → desc → unsorted.
  const [lastUsedOrder, setLastUsedOrder] = useState<CredentialOrder | undefined>(undefined)

  const credentials = useCredentials(lastUsedOrder)
  const persons = usePersons()

  const cycleLastUsedOrder = () =>
    setLastUsedOrder((prev) =>
      prev === undefined ? 'last_used_at' : prev === 'last_used_at' ? '-last_used_at' : undefined,
    )

  const personNames = new Map((persons.data ?? []).map((p) => [p.unique_id, p]))

  const block = useMutation({
    mutationFn: api.blockCredential,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope] }),
  })
  const unblock = useMutation({
    mutationFn: api.unblockCredential,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [scope] }),
  })
  const mutating = block.isPending || unblock.isPending

  const filtered = (credentials.data ?? []).filter(
    (c) =>
      (kindFilter === 'all' || c.kind === kindFilter) &&
      (statusFilter === 'all' || c.status === statusFilter),
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Directory"
        title="Credentials"
        action={<Button onClick={() => setIssueOpen(true)}>+ Issue credential</Button>}
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="tnum rounded-sm border border-ostrel-gold/50 bg-ostrel-gold/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-ostrel-gold">
          All credentials {credentials.data?.length ?? '–'}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Kind
          </span>
          <FilterChip active={kindFilter === 'all'} label="All" onClick={() => setKindFilter('all')} />
          {CREDENTIAL_KINDS.map((k) => (
            <FilterChip
              key={k}
              active={kindFilter === k}
              label={kindLabel(k)}
              onClick={() => setKindFilter(kindFilter === k ? 'all' : k)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[9px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Status
          </span>
          <FilterChip active={statusFilter === 'all'} label="All" onClick={() => setStatusFilter('all')} />
          {statusOptions.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter === s}
              label={s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            />
          ))}
        </div>
      </div>

      {credentials.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : credentials.isError ? (
        <EmptyState title="Could not load credentials" hint={credentials.error.message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={credentials.data.length === 0 ? 'No credentials yet' : 'No credentials match the filters'}
          hint={credentials.data.length === 0 ? 'Issue the first credential.' : 'Try widening the filters.'}
          action={
            credentials.data.length === 0 ? (
              <Button size="sm" onClick={() => setIssueOpen(true)}>
                + Issue credential
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Credential</TH>
              <TH>Kind</TH>
              <TH>Type</TH>
              <TH>Holder</TH>
              <TH>Status</TH>
              <TH>Holder validity</TH>
              <TH
                aria-sort={
                  lastUsedOrder === 'last_used_at' ? 'ascending' : lastUsedOrder === '-last_used_at' ? 'descending' : 'none'
                }
              >
                <button
                  type="button"
                  onClick={cycleLastUsedOrder}
                  className="cursor-pointer select-none border-0 bg-transparent p-0 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted hover:text-ostrel-ink focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                >
                  Last used{lastUsedOrder === 'last_used_at' ? ' ▲' : lastUsedOrder === '-last_used_at' ? ' ▼' : ''}
                </button>
              </TH>
              <TH>Last location</TH>
              <TH className="text-right">Action</TH>
            </tr>
          </THead>
          <TBody>
            {filtered.map((c) => {
              const holder = c.person ? personNames.get(c.person) : undefined
              // #61: credentials have no window of their own -- the holder's applies.
              const validity = holder ? validityWindow(holder.valid_from, holder.valid_to) : null
              return (
                <TR
                  key={c.unique_id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/credentials/${c.unique_id}`)}
                >
                  <TD>
                    <span className="tnum block font-mono text-sm text-ostrel-ink">{c.code}</span>
                    <span className="tnum block text-[10px] text-ostrel-ink-muted">{c.unique_id}</span>
                  </TD>
                  <TD>
                    <Chip tone="muted" label={kindLabel(c.kind)} />
                  </TD>
                  <TD>
                    <span className="text-sm text-ostrel-ink">{c.subtype || '—'}</span>
                  </TD>
                  <TD>
                    {c.person ? (
                      <span className="min-w-0">
                        {/* stopPropagation: the row itself opens the credential. */}
                        <Link
                          to={`/directory/${c.person}`}
                          onClick={(e) => e.stopPropagation()}
                          className="block truncate text-sm font-medium text-ostrel-ink hover:text-ostrel-accent"
                        >
                          {holder?.display_name || c.person}
                        </Link>
                        <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                          {holder?.emails[0] ?? c.person}
                        </span>
                      </span>
                    ) : (
                      <span className="text-xs text-ostrel-ink-muted">Unassigned</span>
                    )}
                  </TD>
                  <TD>
                    <Chip tone={credentialStatusTone(c.status)} label={c.status} />
                  </TD>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink-muted">{validity ?? 'No expiry'}</span>
                  </TD>
                  <TD>
                    <span className="tnum text-xs text-ostrel-ink-muted" title={lastUsedTitle(c)}>
                      {lastUsedRelative(c)}
                    </span>
                  </TD>
                  <TD>
                    {c.last_used_location ? (
                      <Link
                        to={`/locations/${c.last_used_location}`}
                        onClick={(e) => e.stopPropagation()}
                        className="truncate text-xs text-ostrel-ink hover:text-ostrel-accent"
                      >
                        {c.last_used_location_name || c.last_used_location}
                      </Link>
                    ) : (
                      <span className="text-xs text-ostrel-ink-muted">—</span>
                    )}
                  </TD>
                  <TD className="text-right">
                    {/* A credential bound to a dry-run system is read-only —
                        block/unblock would never reach the portal. */}
                    {(() => {
                      const hint = readOnlyReason(c)
                      return c.status === 'blocked' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={mutating || hint !== undefined}
                          title={hint}
                          onClick={(e) => {
                            e.stopPropagation()
                            unblock.mutate(c.unique_id)
                          }}
                        >
                          Unblock
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={mutating || hint !== undefined}
                          title={hint}
                          onClick={(e) => {
                            e.stopPropagation()
                            block.mutate(c.unique_id)
                          }}
                        >
                          Block
                        </Button>
                      )
                    })()}
                  </TD>
                </TR>
              )
            })}
          </TBody>
        </Table>
      )}
      {(block.isError || unblock.isError) && (
        <p className="text-xs text-ostrel-danger">{(block.error ?? unblock.error)?.message}</p>
      )}

      <CredentialFormModal open={issueOpen} onClose={() => setIssueOpen(false)} />
    </div>
  )
}
